import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { getImpactText } from "../utils";
import type { CheckInFormData } from "../types";

interface UseCheckInSubmitOptions {
	userId: string | undefined;
	onSuccess: () => void;
}

interface UseCheckInSubmitReturn {
	submitting: boolean;
	submitCheckIn: (formData: CheckInFormData) => Promise<void>;
}

export function useCheckInSubmit({
	userId,
	onSuccess,
}: UseCheckInSubmitOptions): UseCheckInSubmitReturn {
	const [submitting, setSubmitting] = useState(false);

	const submitCheckIn = async (formData: CheckInFormData) => {
		if (!userId) return;

		const {
			generalFeeling,
			dayDescription,
			selectedReflections,
			reflectionResponses,
		} = formData;

		if (![3, 1, -1, -3].includes(generalFeeling)) {
			alert("Please select a valid feeling rating");
			return;
		}

		if (!dayDescription.trim()) {
			alert("Please provide a description of your day");
			return;
		}

		setSubmitting(true);
		try {
			let reflection_id: string | null = null;
			let reflection_feeling: number | null = null;
			let reflection_impact: string | null = null;
			let reflection_reasons: string | null = null;

			if (selectedReflections !== null) {
				reflection_id = selectedReflections;
				const resp = reflectionResponses[selectedReflections];
				if (resp) {
					reflection_feeling =
						resp.impact !== null ? Number(resp.impact) : null;
					reflection_impact =
						resp.impact !== null ? getImpactText(Number(resp.impact)) : null;
					reflection_reasons = resp.reasons || null;
				}
			}

			const { error } = await supabase.from("daily_checkins").insert({
				user_id: userId,
				general_feeling: generalFeeling,
				day_description: dayDescription,
				signals_noticed: [],
				corrective_actions_taken: [],
				reflection_id,
				reflection_feeling,
				reflection_impact,
				reflection_reasons,
			});

			if (!error) {
				console.log("✅ Check-in saved successfully");
				onSuccess();
			} else {
				console.error("Error submitting check-in:", error);
				alert(
					`Failed to save check-in: ${error.message || "Unknown error"}. Please try again.`,
				);
			}
		} catch (error) {
			console.error("Error submitting check-in:", error);
			alert(
				`Failed to save check-in: ${error instanceof Error ? error.message : "Unknown error"}. Please try again.`,
			);
		} finally {
			setSubmitting(false);
		}
	};

	return {
		submitting,
		submitCheckIn,
	};
}
